/**
 * OAuth Callback Page - Story 3.1
 *
 * Handles OAuth redirect from Cognito after social login (Google, Apple)
 * Processes authorization code and redirects user to appropriate page
 *
 * Route: /auth/callback
 */
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { handleOAuthCallback, selectReturnUrl, clearReturnUrl, selectIsLoading, selectError } from "@/store/slices/authSlice";
import { selectHasSeenFirstLoginWelcome } from "@/store/slices/onboardingSlice";
import { AuthLayout } from "@/ui/layouts";

/**
 * CallbackPage Component
 *
 * Pure MVVM page component - displays loading/error states only
 * All logic handled by Redux thunk
 */
export default function CallbackPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const loading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const returnUrl = useAppSelector(selectReturnUrl);
  const hasSeenWelcome = useAppSelector(selectHasSeenFirstLoginWelcome);
  const [processingComplete, setProcessingComplete] = React.useState(false);

  React.useEffect(() => {
    // Extract OAuth callback parameters
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");
    const code = searchParams.get("code");

    // Handle OAuth error
    if (error) {
      console.error("OAuth error:", error, errorDescription);
      // Redirect to login with error message
      navigate(`/auth/login?error=${encodeURIComponent(errorDescription || error)}`, {
        replace: true,
      });
      return;
    }

    // Handle OAuth success (authorization code present)
    if (code && !processingComplete) {
      setProcessingComplete(true);

      // Process OAuth callback with Redux thunk
      dispatch(handleOAuthCallback())
        .unwrap()
        .then(() => {
          // Successful authentication - check for return URL (e.g., from NFC page)
          if (returnUrl) {
            // User came from a protected page (like NFC pairing) - return there
            dispatch(clearReturnUrl());
            navigate(returnUrl, { replace: true });
          } else if (!hasSeenWelcome) {
            // First login - show welcome page (Story 3.9.a)
            navigate("/welcome", { replace: true });
          } else {
            // Returning user - redirect to profile
            navigate("/owner/profile", { replace: true });
          }
        })
        .catch((err) => {
          // Authentication failed - redirect to login with error
          console.error("OAuth callback processing failed:", err);
          navigate(
            `/login?error=${encodeURIComponent("Authentication failed. Please try again.")}`,
            { replace: true }
          );
        });
    }
  }, [searchParams, dispatch, navigate, processingComplete]);

  // Show error state if present
  if (error) {
    return (
      <AuthLayout>
        <div className="text-center space-y-4">
          <div className="text-brand-red text-[1.375rem] font-semibold font-montserrat">
            Authentication Error
          </div>
          <p className="text-gray-700 text-[0.9375rem] font-montserrat">
            {error}
          </p>
          <button
            onClick={() => navigate("/auth/login", { replace: true })}
            className="text-brand-red text-[0.9375rem] font-montserrat underline"
          >
            Return to Login
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Show loading state while processing OAuth callback
  return (
    <AuthLayout>
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          {/* Loading spinner */}
          <div className="w-12 h-12 border-4 border-gray-200 border-t-brand-red rounded-full animate-spin" />
        </div>
        <div className="text-gray-900 text-[1.125rem] font-semibold font-montserrat">
          {loading ? "Completing sign in..." : "Processing authentication..."}
        </div>
        <p className="text-gray-500 text-[0.9375rem] font-montserrat">
          Please wait while we log you in
        </p>
      </div>
    </AuthLayout>
  );
}
